import React, { useState, useCallback } from "react";
import axios from "axios";
import { Calendar, MapPin, Clock, UserX, Users, StickyNote, Video, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { API } from "@/utils/constants";

const formatDateNice = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const extractVenue = (location) => {
  if (!location) return null;
  const parts = location.split(/\s+(?=\d|[A-Z]{1,2}\d)/);
  return parts[0]?.trim() || location;
};

export default function DateCheckPage() {
  const [selectedDate, setSelectedDate] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheck = useCallback(async (date) => {
    if (!date) return;
    setLoading(true);
    setError("");
    try {
      const resp = await axios.get(`${API}/date-check/${date}`);
      setResult(resp.data);
    } catch {
      setError("Failed to check date. Please try again.");
      setResult(null);
    }
    setLoading(false);
  }, []);

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    if (date) handleCheck(date);
  };

  return (
    <div className="min-h-screen bg-[#f9fafb]">
      <header className="bg-white border-b border-[#e5e7eb]">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <h1 className="font-heading text-xl text-[#605e5e]">WVE Date Checker</h1>
          <p className="text-sm text-[#8d8b8b] mt-0.5">Check what's happening on a specific date</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-[#e5e7eb] p-6">
          <label className="text-sm font-medium text-[#605e5e] mb-2 block">Select a date</label>
          <div className="flex gap-3">
            <Input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="flex-1 h-11"
              data-testid="date-check-input"
            />
            <Button
              onClick={() => handleCheck(selectedDate)}
              disabled={!selectedDate || loading}
              className="bg-[#605e5e] hover:bg-[#4a4848] text-white h-11 px-5"
              data-testid="date-check-btn"
            >
              <Search className="h-4 w-4 mr-2" />
              Check
            </Button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        {loading && (
          <div className="mt-8 text-center text-[#8d8b8b]">
            <div className="h-6 w-6 border-2 border-[#605e5e] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            Checking...
          </div>
        )}

        {result && !loading && (
          <div className="mt-6 space-y-4" data-testid="date-check-results">
            {/* Date header */}
            <div className="bg-[#605e5e] rounded-xl p-4 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-5 w-5" />
                <span className="font-heading text-lg">{formatDateNice(result.date)}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-white/80">
                <span>{result.jobs.length} job{result.jobs.length !== 1 ? "s" : ""}</span>
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {result.available_count}/{result.total_team} available
                </span>
              </div>
            </div>

            {/* Unavailable */}
            {result.unavailable.length > 0 && (
              <div className="bg-white rounded-xl border border-[#e5e7eb] p-4" data-testid="date-check-unavailable">
                <h3 className="text-xs font-medium text-[#8d8b8b] uppercase tracking-wide mb-2">Unavailable</h3>
                <div className="flex flex-wrap gap-1.5">
                  {result.unavailable.map(name => (
                    <Badge key={name} className="bg-red-50 text-red-700 border border-red-200 text-xs">
                      <UserX className="h-3 w-3 mr-1" />
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {result.notes.length > 0 && (
              <div className="bg-white rounded-xl border border-[#e5e7eb] p-4" data-testid="date-check-notes">
                <h3 className="text-xs font-medium text-[#8d8b8b] uppercase tracking-wide mb-2">Notes</h3>
                <div className="flex flex-wrap gap-1.5">
                  {result.notes.map((note, i) => (
                    <Badge key={`${note.title}-${i}`} className="bg-teal-50 text-teal-800 border border-teal-200 text-xs">
                      <StickyNote className="h-3 w-3 mr-1" />
                      {note.title}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Jobs */}
            {result.jobs.length > 0 ? (
              <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden" data-testid="date-check-jobs">
                <h3 className="text-xs font-medium text-[#8d8b8b] uppercase tracking-wide px-4 pt-4 pb-2">Jobs</h3>
                <div className="divide-y divide-[#e5e7eb]">
                  {result.jobs.map(job => (
                    <div key={job.id} className="px-4 py-3" data-testid={`date-check-job-${job.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-[#1f2937]">
                              {job.event_name || job.title}
                            </span>
                            {job.studio_type && (
                              <span className="text-[10px] font-medium bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">
                                {job.studio_type.replace(" Photoshoot", "").replace(" Studio Hire", "")}
                              </span>
                            )}
                            {job.has_video_booth && (
                              <Video className="h-3.5 w-3.5 text-[#8DA399]" />
                            )}
                          </div>
                          {job.location && (
                            <p className="text-xs text-[#6b7280] mt-0.5 flex items-center gap-1">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{extractVenue(job.location)}</span>
                            </p>
                          )}
                          {job.team.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {job.team.map(name => (
                                <Badge key={name} className="bg-[#8DA399] text-white text-[10px] px-1.5 py-0">
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {job.time && (
                          <div className="flex items-center gap-1 text-xs text-[#605e5e] font-medium flex-shrink-0 bg-gray-100 px-2 py-1 rounded">
                            <Clock className="h-3 w-3" />
                            {job.time}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#e5e7eb] p-6 text-center">
                <p className="text-sm text-[#8d8b8b] italic">No jobs on this date</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
